"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Home, Search, ArrowLeft, Compass } from "lucide-react"
import { motion } from "framer-motion"
import Link from "next/link"

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.08,
            delayChildren: 0.1,
        },
    },
}

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.4, ease: "easeOut" as const },
    },
}

const numberVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            type: "spring" as const,
            stiffness: 200,
            damping: 20,
        },
    },
}

export default function NotFound() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-green-50 to-teal-100 p-4 overflow-hidden">
            <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="relative z-10 w-full max-w-lg"
            >
                <Card className="border-emerald-200/50 bg-white/70 backdrop-blur-md shadow-2xl shadow-emerald-100/50">
                    <CardContent className="p-10 text-center">
                        {/* 404 Number — simplified spring, no rotateY */}
                        <div className="flex items-center justify-center gap-2 mb-6">
                            {['4', '0', '4'].map((num, index) => (
                                <motion.span
                                    key={index}
                                    variants={numberVariants}
                                    className="text-7xl md:text-8xl font-black bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500 bg-clip-text text-transparent"
                                >
                                    {num}
                                </motion.span>
                            ))}
                        </div>

                        {/* Compass icon — CSS animation instead of framer infinite loop */}
                        <motion.div variants={itemVariants} className="mb-6">
                            <div className="h-20 w-20 rounded-full bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center mx-auto shadow-lg shadow-emerald-200/50 animate-float">
                                <Compass className="h-10 w-10 text-emerald-600" />
                            </div>
                        </motion.div>

                        <motion.h1
                            variants={itemVariants}
                            className="text-2xl font-bold mb-3 text-gray-800"
                        >
                            Page Not Found
                        </motion.h1>

                        <motion.p
                            variants={itemVariants}
                            className="text-gray-600 mb-8"
                        >
                            Oops! The page you're looking for seems to have wandered off into the blockchain void.
                        </motion.p>

                        {/* Action buttons */}
                        <motion.div
                            variants={itemVariants}
                            className="flex flex-col sm:flex-row gap-3 mb-6"
                        >
                            <Link href="/" className="flex-1 block">
                                <Button
                                    className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-lg shadow-emerald-200/50 transition-transform active:scale-95"
                                >
                                    <Home className="h-4 w-4 mr-2" />
                                    Go Home
                                </Button>
                            </Link>

                            <Button
                                variant="outline"
                                onClick={() => window.history.back()}
                                className="flex-1 border-emerald-200 bg-white/50 hover:bg-emerald-50 text-emerald-700 transition-transform active:scale-95"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Go Back
                            </Button>
                        </motion.div>

                        {/* Search suggestion */}
                        <motion.div
                            variants={itemVariants}
                            className="flex items-center justify-center gap-2 text-sm text-gray-500"
                        >
                            <Search className="h-4 w-4" />
                            <span>Looking for presales? Try our</span>
                            <Link href="/presales" className="text-emerald-600 hover:text-emerald-700 underline underline-offset-2 font-medium">
                                explore page
                            </Link>
                        </motion.div>

                        <motion.div
                            variants={itemVariants}
                            className="mt-8 pt-6 border-t border-emerald-100"
                        >
                            <p className="text-xs text-gray-500">
                                TokenKickstarter - Decentralized Token Launchpad
                            </p>
                        </motion.div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    )
}
